import { MAX_INDEX_COLORS, getSelectionIdentityColor } from './colorScale';
import { getGroupKeyFromItem, groupKeyEquals, getDimensionItemColorGetter } from './colorGrouping';
import type { SelectionRangeColorResolver } from './selectionRangeHelpers';

export { MAX_INDEX_COLORS };

export const DEFAULT_UNSELECTED_COLOR = 'lightgray';
export const UNSELECTED_TRACK_COLOR = 'lightgrey';

export function getItemSelectionId(item: Record<string, unknown>): number | string | undefined {
  const raw = item.event_id ?? item.id;
  if (raw === undefined || raw === null) return undefined;
  return raw as number | string;
}

/**
 * Selection membership tolerant of string-vs-number event ids.
 *
 * Rows arriving from different endpoints spell event_id inconsistently, and a strict Array.includes miss
 * silently downgraded a selected item to the unselected/fallback color — on a map range that reads as the
 * overlay never appearing at all.
 */
function isSelectedId(
  selectedIds: readonly (number | string)[],
  itemId: number | string | undefined
): boolean {
  if (itemId === undefined) return false;
  if (selectedIds.includes(itemId)) return true;
  const target = Number(itemId);
  if (!Number.isFinite(target)) return false;
  return selectedIds.some((value) => Number(value) === target);
}

export interface ResolveItemColorContext {
  selectedIds: readonly (number | string)[];
  legendSelectionActive: boolean;
  colorMode: string;
  isGrouped?: boolean;
  selectedGroupKeys?: readonly (string | number)[];
  getDimensionColor: (item: unknown) => string;
  unselectedColor?: string;
}

/**
 * Unified selection + dimension color resolver for map, timeseries, scatter, table, and video.
 * Legend-driven selections keep dimension colors; direct multi-select uses the index palette.
 */
export function resolveItemColor(
  item: Record<string, unknown>,
  ctx: ResolveItemColorContext
): string {
  const {
    selectedIds,
    legendSelectionActive,
    colorMode,
    isGrouped = false,
    selectedGroupKeys = [],
    getDimensionColor,
    unselectedColor = DEFAULT_UNSELECTED_COLOR,
  } = ctx;

  const mode = String(colorMode || '').toUpperCase();
  const itemId = getItemSelectionId(item);

  if (isGrouped) {
    const key = getGroupKeyFromItem(item, mode);
    const groupSelected = selectedGroupKeys.some((k) => groupKeyEquals(k, key));
    return groupSelected ? getDimensionColor(item) : unselectedColor;
  }

  if (mode === 'SOURCE') {
    return getDimensionColor(item);
  }

  if (selectedIds.length > MAX_INDEX_COLORS) {
    if (isSelectedId(selectedIds, itemId)) {
      return getDimensionColor(item);
    }
    return selectedIds.length > 0 ? unselectedColor : getDimensionColor(item);
  }

  if (selectedIds.length === 0) {
    return getDimensionColor(item);
  }

  if (!isSelectedId(selectedIds, itemId)) {
    return unselectedColor;
  }

  if (legendSelectionActive) {
    return getDimensionColor(item);
  }

  return getSelectionIdentityColor(itemId, selectedIds) ?? getDimensionColor(item);
}

/** Background / outline color for a row or tile known to be selected. */
export function resolveSelectedRowBackgroundColor(
  item: Record<string, unknown>,
  ctx: ResolveItemColorContext & { fallback?: string; forceIndexColors?: boolean }
): string {
  const fallback = ctx.fallback ?? 'white';
  const mode = String(ctx.colorMode || '').toUpperCase();
  const itemId = getItemSelectionId(item);
  if (!isSelectedId(ctx.selectedIds, itemId)) {
    return fallback;
  }

  if (mode === 'SOURCE') {
    return ctx.getDimensionColor(item);
  }

  if (ctx.selectedIds.length > MAX_INDEX_COLORS) {
    return ctx.getDimensionColor(item);
  }

  if (ctx.forceIndexColors) {
    return getSelectionIdentityColor(itemId, ctx.selectedIds) ?? fallback;
  }

  if (ctx.legendSelectionActive) {
    return ctx.getDimensionColor(item);
  }

  // getSelectionIdentityColor rather than getIndexColor: same palette lookup, but numeric-tolerant, and it
  // returns undefined instead of 'lightgray' on a miss so `fallback` actually applies.
  return getSelectionIdentityColor(itemId, ctx.selectedIds) ?? fallback;
}

export type ManeuverRangeColorResolverOptions = {
  colorMode: string;
  selectedIds: readonly (number | string)[];
  legendSelectionActive: boolean;
  lookupRows: Record<string, unknown>[];
  forceIndexColors?: boolean;
  /**
   * Color returned for a row that isn't in the selection. Defaults to "white" (table/tile background).
   * Map ranges should pass `undefined` so the range carries no color and the consumer falls back to the
   * event's palette index instead of painting an invisible white overlay.
   */
  unselectedFallback?: string | undefined;
};

/** Map highlight colors matching maneuver table/timeline selection (ScatterTimeseries + DataTable). */
export function createManeuverRangeColorResolver(
  options: ManeuverRangeColorResolverOptions
): SelectionRangeColorResolver {
  const { colorMode, selectedIds, legendSelectionActive, lookupRows, forceIndexColors } = options;
  const fallback = 'unselectedFallback' in options ? options.unselectedFallback : "white";
  const getDimensionColorFn = getDimensionItemColorGetter(lookupRows, colorMode);

  return (row, _eventId) => {
    if (!row) return undefined;
    // Unselected rows never reach a color decision below, so short-circuit here: that's the only way a
    // caller can ask for "no color" rather than the "white" the row-background helper always produces.
    if (!isSelectedId(selectedIds, getItemSelectionId(row))) return fallback;
    return resolveSelectedRowBackgroundColor(row, {
      selectedIds,
      legendSelectionActive,
      colorMode,
      getDimensionColor: (item) =>
        getDimensionColorFn?.(item as Record<string, unknown>) ?? DEFAULT_UNSELECTED_COLOR,
      fallback: fallback ?? "white",
      forceIndexColors,
    });
  };
}
